import {useEffect, useState} from 'react';

import {ProvidersDentalGuide} from 'domain/usecases';
import {PlanTypeEnum} from 'presentation/constants/bookingTypesEnum';

import {useLazyScroll} from 'presentation/hooks/useLazyScroll';
import {useFilteredOdontoProviders} from 'presentation/modules/guide/odonto/hooks/useFilteredOdontoProviders';
import {GuideStep} from 'presentation/modules/guide/shared/constants/constants';
import {useGuideNavigation} from 'presentation/modules/guide/shared/hooks/useGuideNavigation/useGuideNavigation';
import {useGuideQueryParams} from 'presentation/modules/guide/shared/hooks/useGuideQueryParams';
import {formattedDistrictNames} from 'presentation/modules/guide/shared/utils/formattedDistrictNames';
import {useAccountInformation, useMediaQueries, useQueryParams} from 'presentation/pages/private';
import {QueryParamsEnum} from 'presentation/constants';

export function useOdontoGuide() {
	const {isMobile} = useMediaQueries();
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const {deleteAllQueryParams} = useGuideQueryParams();
	const {isEarlyAccess, beneficiary} = useAccountInformation();
	const [isEarlyAccessModalOpen, setIsEarlyAccessModalOpen] = useState(false);

	useGuideNavigation({type: PlanTypeEnum.ODONTO});

	const {
		ufCode,
		cityName,
		serviceType,
		specialtyCode,
		specialtyName,
		coordinates,
		step: currentStepState,
		districtNames,
	} = SEARCH_PARAMS;

	const isGeoFlow = !!coordinates;

	const {
		data: providers = [],
		allProviders,
		isFetching,
		isError: providersError,
		refetchProvidersQuery,
	} = useFilteredOdontoProviders({
		coordenadas: coordinates,
		cdEspecialidade: specialtyCode,
		dsTipo: serviceType,
		filtroBairro: formattedDistrictNames(districtNames, ufCode, cityName, isGeoFlow),
		type: PlanTypeEnum.ODONTO,
	});

	const {items, scrollableContentRef, loadingNewPageState, handleVisible} =
		useLazyScroll<ProvidersDentalGuide>({
			itemsList: providers,
			itemsPerPage: 16,
			loadingTime: 350,
		});

	const shouldRenderEmptyState = !providersError && providers?.length === 0;

	const onSearchResults = () => {
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.STEP, GuideStep.RESULTS);
			return prev;
		});
	};

	useEffect(() => {
		if (!currentStepState) {
			deleteAllQueryParams();
		}
	}, []);

	useEffect(() => {
		if (isEarlyAccess && !currentStepState) {
			setIsEarlyAccessModalOpen(true);
		}
	}, [isEarlyAccess, currentStepState]);

	return {
		currentStepState,
		isMobile,
		providers: items,
		scrollableContentRef,
		loadingNewPageState,
		handleVisible,
		providersError,
		shouldRenderEmptyState,
		allProviders,
		isFetching,
		refetchProvidersQuery,
		specialtyName,
		isEarlyAccess,
		isEarlyAccessModalOpen,
		setIsEarlyAccessModalOpen,
		beneficiary,
		onSearchResults,
	};
}
